import { allPages } from "@/lib/pagination";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/apiClient";
import { useWorkspace } from "@/context/WorkspaceContext";
import { PageHeader, EmptyState } from "@/components/PageBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Radio, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Rooms() {
  const { projectId, currentProject } = useWorkspace();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("conference");
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!projectId) return;
    allPages(`/projects/${projectId}/rooms`).then((data) => setRooms(data)).catch(() => {});
  };
  useEffect(load, [projectId]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/rooms`, { name, type });
      toast.success("Room created");
      setOpen(false); setName(""); setType("conference");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const createBtn = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-room-button" disabled={!projectId} className="bg-zinc-50 text-zinc-900 hover:bg-zinc-200">
          <Plus className="mr-2 h-4 w-4" /> New room
        </Button>
      </DialogTrigger>
      <DialogContent className="border-zinc-800 bg-zinc-950" data-testid="new-room-dialog">
        <DialogHeader><DialogTitle className="text-zinc-100">Create room</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-zinc-300">Room name</Label>
            <Input data-testid="room-name-input" value={name} onChange={(e) => setName(e.target.value.replace(/[^a-zA-Z0-9_-]/g, ""))}
              className="bg-zinc-950 border-zinc-800 font-mono" placeholder="team-standup" />
            <p className="text-xs text-zinc-500">Letters, numbers, dashes and underscores only.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="room-type-select" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-950">
                <SelectItem value="audio">Audio</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="conference">Conference</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="create-room-confirm" disabled={!name || busy} onClick={create}
            className="bg-zinc-50 text-zinc-900 hover:bg-zinc-200">{busy ? "Creating…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div data-testid="rooms-page">
      <PageHeader title="Rooms" description={`Live sessions in ${currentProject?.name || "your project"}.`} actions={createBtn} />
      {!projectId ? (
        <EmptyState title="No project selected" description="Create or select a project first." />
      ) : rooms.length === 0 ? (
        <EmptyState testId="rooms-empty" title="No rooms yet"
          description="Create a room, then generate participant tokens from your backend using an API key." action={createBtn} />
      ) : (
        <div className="overflow-hidden rounded-md border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/50 text-left text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-4 py-3 font-medium">Room</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Live</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {rooms.map((r) => (
                <tr key={r.id} className="cursor-pointer transition-colors hover:bg-zinc-900/40"
                  data-testid={`room-row-${r.name}`} onClick={() => navigate(`/dashboard/rooms/${r.id}`)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Radio className="h-4 w-4 text-zinc-500" />
                      <span className="font-mono text-zinc-200">{r.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{r.type}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400">
                      <span className={`h-1.5 w-1.5 rounded-full ${r.active_participants > 0 ? "bg-emerald-500 animate-pulse" : "bg-zinc-600"}`} />
                      {r.active_participants > 0 ? "live" : r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-300">{r.active_participants}</td>
                  <td className="px-4 py-3 text-right"><ArrowRight className="ml-auto h-4 w-4 text-zinc-600" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
